import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stack, Typography, Box, Alert, CircularProgress, Divider,
  alpha, useTheme
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { promotionsApi } from '../../services/sessions/promotionsApi';
import { PromotionPreviewRow, SessionClass } from '../../types/sessions';
import StatusChip from '../common/StatusChip';

interface CommitConfirmationDialogProps {
  open: boolean;
  runId: string;
  classId: string;
  className: string;
  /** When non-empty, commit only these enrollments. Empty = commit all decided in class. */
  enrollmentIds: string[];
  /** The current preview rows (filtered to the IDs above when applicable). */
  rows: PromotionPreviewRow[];
  classes: SessionClass[];
  onClose: () => void;
  onCommitted: () => void;
}

const COOLDOWN_SECS = 3;

const CommitConfirmationDialog: React.FC<CommitConfirmationDialogProps> = ({
  open, runId, classId, className, enrollmentIds, rows, classes, onClose, onCommitted
}) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [cooldown, setCooldown] = useState(COOLDOWN_SECS);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCooldown(COOLDOWN_SECS);
    const t = setInterval(() => setCooldown(c => (c > 0 ? c - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [open]);

  const targetClasses = useMemo(
    () => new Map(classes.map(c => [String(c._id), c])),
    [classes]
  );

  // Bucket the decisions for the summary cards.
  const summary = useMemo(() => {
    const buckets = { promote: 0, retain: 0, graduate: 0, drop: 0, manual: 0 };
    for (const r of rows) {
      switch (r.effectiveDecision) {
        case 'promote':         buckets.promote += 1; break;
        case 'retain':          buckets.retain += 1; break;
        case 'graduate':        buckets.graduate += 1; break;
        case 'drop':            buckets.drop += 1; break;
        case 'manual_override': buckets.manual += 1; break;
      }
    }
    return buckets;
  }, [rows]);

  const handleCommit = async () => {
    setSubmitting(true);
    try {
      const result = await promotionsApi.commitChunk(runId, {
        classId,
        enrollmentIds: enrollmentIds.length > 0 ? enrollmentIds : undefined
      });
      const created = result.createdEnrollmentIds.length;
      enqueueSnackbar(
        created > 0
          ? `${created} student${created === 1 ? '' : 's'} moved into the next session`
          : 'No new enrollments — terminal decisions only',
        { variant: 'success' }
      );
      onCommitted();
      onClose();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Commit failed', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const total = rows.length;
  const selectionMode = enrollmentIds.length > 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <CheckCircleOutline color="success" fontSize="small" />
          <Typography variant="h6" fontWeight={600}>Confirm commit</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {selectionMode
            ? <>You're about to commit <strong>{total}</strong> selected student{total === 1 ? '' : 's'} in <strong>{className}</strong>.</>
            : <>You're about to commit <strong>{total}</strong> decided student{total === 1 ? '' : 's'} in <strong>{className}</strong>.</>}
          {' '}This creates the next-session enrollment rows and closes the source rows.
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={2}>
          {/* Summary tiles */}
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {summary.promote  > 0 && <SummaryTile label="Promoted"  count={summary.promote}  tone="success" />}
            {summary.retain   > 0 && <SummaryTile label="Retained"  count={summary.retain}   tone="highlight" />}
            {summary.graduate > 0 && <SummaryTile label="Graduated" count={summary.graduate} tone="info" />}
            {summary.drop     > 0 && <SummaryTile label="Dropped"   count={summary.drop}     tone="warning" />}
            {summary.manual   > 0 && <SummaryTile label="Manual"    count={summary.manual}   tone="primary" />}
          </Stack>

          <Divider />

          {/* Sample audit line */}
          <Alert severity="info" variant="outlined">
            <Typography variant="body2">
              Each row's snapshotted target class and section will be used as written — you'll see the exact
              destination per student in their timeline once the commit completes.
            </Typography>
          </Alert>

          <Box sx={{
            px: 1.5, py: 1, borderRadius: 1.5,
            bgcolor: alpha(theme.palette.warning.main, 0.06),
            border: `1px solid ${alpha(theme.palette.warning.main, 0.22)}`
          }}>
            <Typography variant="caption" color="text.secondary">
              Committed enrollments become read-only. If you make a mistake, you can fix it later via
              Transfer or Exit on the new-session enrollment.
            </Typography>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="success"
          disableElevation
          disabled={submitting || cooldown > 0}
          onClick={handleCommit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {submitting ? 'Committing…' : cooldown > 0 ? `Commit (${cooldown})` : `Commit ${total} student${total === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const SummaryTile: React.FC<{ label: string; count: number; tone: 'success' | 'highlight' | 'info' | 'warning' | 'primary' }> = ({ label, count, tone }) => {
  const theme = useTheme();
  const color =
    tone === 'success'   ? theme.palette.success.main :
    tone === 'highlight' ? theme.palette.primary.main :
    tone === 'info'      ? theme.palette.info.main :
    tone === 'warning'   ? theme.palette.warning.main :
                           theme.palette.primary.main;
  return (
    <Box sx={{
      px: 1.5, py: 1, borderRadius: 1.5, minWidth: 96,
      bgcolor: alpha(color, 0.10),
      border: `1px solid ${alpha(color, 0.28)}`
    }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.1 }}>{count}</Typography>
    </Box>
  );
};

export default CommitConfirmationDialog;
